import { useMemo } from "react";
import { generateHTML } from "@tiptap/core";
import { useTextBlockInteraction } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/RichText/useTextBlockInteraction";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import {
  createTipTapFromText,
  isRichTextContent,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/richTextHelpers";
import type { TipTapContent } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textTypes";
import { useSgidContext } from "./SgIdProvider";
import type { LinkStyles } from "./Utils/getLinkStyles";
import {
  VisualEmailTiptapEditor,
  getVisualEmailTiptapExtensions,
} from "./VisualEmailTiptapEditor";

export const convertTiptapToHTML = ({
  content,
  sgIdToObjectMap,
  isPreviewMode,
  linkStyles,
}: {
  content: TipTapContent;
  sgIdToObjectMap?: Record<string, unknown>;
  isPreviewMode?: boolean;
  linkStyles?: LinkStyles;
}): string => {
  if (!content?.content) return "";

  return generateHTML(
    content,
    getVisualEmailTiptapExtensions({
      sgIdToObjectMap,
      isPreviewMode,
      linkStyles,
    }),
  );
};

interface VisualEmailTextRendererProps {
  blockPath: string;
  className?: string;
  isPreviewMode?: boolean;
  linkStyles?: LinkStyles;
  placeholder?: string;
  text: string | TipTapContent | undefined;
}

export const VisualEmailTextRenderer = ({
  blockPath,
  text = "",
  className,
  placeholder,
  isPreviewMode,
  linkStyles,
}: VisualEmailTextRendererProps) => {
  const isInlineEditing = useBuilderStore(state => state.isInlineEditing);
  const isCurrentlyInlineEditing = isInlineEditing(blockPath);
  const { handleDoubleClick, handleBlur } = useTextBlockInteraction({
    blockPath,
    text,
  });
  const { localSgidToObjectMap } = useSgidContext();

  const htmlContent = useMemo(() => {
    if (isRichTextContent(text)) {
      return convertTiptapToHTML({
        content: text,
        sgIdToObjectMap: localSgidToObjectMap,
        isPreviewMode,
        linkStyles,
      });
    }
    return null;
  }, [text, isPreviewMode, localSgidToObjectMap, linkStyles]);

  if (isCurrentlyInlineEditing) {
    const editorValue = isRichTextContent(text)
      ? text
      : createTipTapFromText(text);
    return (
      <VisualEmailTiptapEditor
        value={editorValue}
        onBlur={handleBlur}
        placeholder={placeholder}
        shouldFocusWhenReady
      />
    );
  }

  if (htmlContent) {
    return (
      <div
        className={className}
        onDoubleClick={handleDoubleClick}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }

  if (!isRichTextContent(text)) {
    return <span className={className}>{text || placeholder || ""}</span>;
  }

  return null;
};
